import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData, selectTheme } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import ColorPallet from "../color_pallet/colorPallet";
import { updateThemeData } from "../../functions/updateThemeData";

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  // bring in all four values from your theme slice exactly as before
  const { availableThemes, currentTheme, status, data } = useSelector(
    (state) => state.theme
  );

  // left‐side: rules
  const [rules, setRules] = useState([""]);

  const [customTitle, setCustomTitle] = useState("");
  const [totalPoints, setTotalPoints] = useState(5);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [colors, setColors] = useState({
    text_color: "#000000",
    button_color: "#000000",
    button_text_color: "#ffffff",
  });

  useEffect(() => {
    const c = data?.colors;
    const flat = data && !c && (data.text_color != null || data.button_color != null);
    const source = c || (flat ? data : null);
    if (!source) return;
    setColors((prev) => ({
      text_color: source.text_color ?? prev.text_color,
      button_color: source.button_color ?? prev.button_color,
      button_text_color: source.button_text_color ?? prev.button_text_color,
    }));
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // fetch questions & theme on mount
  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  // when theme data arrives, populate rules + basic theme details
  useEffect(() => {
    if (!data) return;
    setRules(data.rules || [""]);
    setCustomTitle(data.landing_page_title || "");
    const pts = data.points;
    setTotalPoints(
      typeof pts === "number" && !Number.isNaN(pts) ? pts : Number(pts) || 5
    );
  }, [data]);

  // --- rules handlers ---
  const handleRuleChange = (idx, val) => {
    let newVal = val;
    if (val.length > 200) {
      Swal.fire(
        "Limit Exceeded",
        "Rules can be at most 200 characters. Extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 200);
    }
    const arr = [...rules];
    arr[idx] = newVal;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 6) {
      return Swal.fire(
        "Limit Reached",
        "You can only add up to 6 rules.",
        "warning"
      );
    }
    setRules([...rules, ""]);
  };
  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire(
        "At Least One Rule Required",
        "You must have at least one rule.",
        "warning"
      );
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const handleCustomTitleChange = (val) => {
    if (val.length > 500) {
      Swal.fire(
        "Limit Exceeded",
        "Custom title can be at most 500 characters. Extra text has been trimmed.",
        "warning"
      );
      val = val.slice(0, 500);
    }
    setCustomTitle(val);
  };

  const handleTotalPointsChange = (val) => {
    const n = parseInt(String(val).replace(/\D/g, ""), 10);
    if (Number.isNaN(n) || n < 0) {
      setTotalPoints(0);
      return;
    }
    setTotalPoints(Math.min(n, 999999));
  };

  // --- submit all ---
  const handleSubmit = async () => {
    const trimmedRules = rules.map((r) => (r ?? "").trim());

    // Ensure there is at least one rule and no rule row is left blank.
    if (trimmedRules.length === 0 || trimmedRules.every((r) => !r)) {
      await Swal.fire(
        "At Least One Rule Required",
        "Please enter at least one rule.",
        "warning"
      );
      return;
    }

    const firstEmptyIndex = trimmedRules.findIndex((r) => !r);
    if (firstEmptyIndex !== -1) {
      await Swal.fire(
        "Rule Text Required",
        `Rule #${firstEmptyIndex + 1} cannot be empty.`,
        "warning"
      );
      return;
    }

    const payload = {
      data: {
        rules: trimmedRules,
        landing_page_title: customTitle,
        points: totalPoints,
        text_color: colors.text_color,
        button_color: colors.button_color,
        button_text_color: colors.button_text_color,
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      // Swal.fire("Success", "Settings saved successfully!", "success");
      setTimeout(() => navigate("/admin/themeUpdate"), 500);
    } catch (e) {
      Swal.fire(
        "Error",
        e.message || "Failed to save. Please try again.",
        "error"
      );
    }
  };

  return (
    <>
      <ColorPallet isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />


          <div className="back-button-holder">
              <button
                className="back-button"
                onClick={() => navigate("/admin/")}
              >
              <i className="fa-solid fa-arrow-left"></i> Back
              </button>
          </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-left">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-pen"></i> &nbsp; Basic Theme Details
                </div>
                <div className="form-group-v1">
                  <label>Add Custom Title:</label>
                  <textarea
                    value={customTitle}
                    onChange={(e) => handleCustomTitleChange(e.target.value)}
                    placeholder="Custom title for the rules / landing page"
                    className="form-control rules-textarea"
                    rows={3}
                    maxLength={500}
                  />
                  <div className="code">* Maximum 500 characters.</div>
                </div>
              </div>
            </div>

            <div className="rules-page-center">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game Rules
                </div>
                <h3 className="custom-rules-title">Custom rules</h3>
                {rules.map((rule, i) => (
                  <div className="rule-row" key={i}>
                    <span className="rule-number">{i + 1}.</span>
                    <input
                      className="rule-input"
                      type="text"
                      placeholder="Enter rule"
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveRule(i)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <div className="rules-button-container">
                  <div>
                    <button className="add-btn" onClick={handleAddRule}>
                      + Add New Rule
                    </button>
                  </div>
                  <div className="code">
                    * Keep 6 rules at the most. <br></br>* At least 1 rule is
                    required.<br></br>* Character limit of each rule is 200<br></br>
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Color Customization
                </div>
                <div className="pallet-holder">
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.text_color}
                          onChange={handleColorChange("text_color")}
                        />
                        <label>Text Color</label>
                      </div>
                      <div className="color-code">{colors.text_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_color}
                          onChange={handleColorChange("button_color")}
                        />
                        <label>Button color</label>
                      </div>
                      <div className="color-code">{colors.button_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_text_color}
                          onChange={handleColorChange("button_text_color")}
                        />
                        <label>Button Text color</label>
                      </div>
                      <div className="color-code">{colors.button_text_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input pallet-input--number">
                      <label className="total-points-label">Total Points</label>
                      <input
                        type="number"
                        className="form-control rules-total-points-input"
                        min={0}
                        max={999999}
                        value={totalPoints}
                        onChange={(e) => handleTotalPointsChange(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save & Continue
            </button>
           
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
